import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Download,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  Bot,
  CreditCard,
  RefreshCw,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { api } from '../../services/api';
import { MetricCard } from '../../components/admin/cards';
import { LineChart, BarChart, PieChart } from '../../components/admin/charts';
import { DataTable } from '../../components/admin/tables/DataTable';
import { DateRangePicker } from '../../components/admin/reports/DateRangePicker';

type ReportTab = 'revenue' | 'subscriptions' | 'users' | 'ai-costs';

const TAB_CONFIG = [
  { key: 'revenue' as const, label: 'Revenue', icon: DollarSign },
  { key: 'subscriptions' as const, label: 'Subscriptions', icon: CreditCard },
  { key: 'users' as const, label: 'Users', icon: Users },
  { key: 'ai-costs' as const, label: 'AI Costs', icon: Bot },
];

function formatCurrency(cents: number | undefined): string {
  if (cents === undefined) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatCurrencyDollars(dollars: number | undefined): string {
  if (dollars === undefined) return '$0.00';
  return `$${dollars.toFixed(2)}`;
}

function formatNumber(num: number | undefined): string {
  if (num === undefined) return '0';
  return num.toLocaleString();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Event type badge colors
const EVENT_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  created: { bg: 'bg-green-100', text: 'text-green-700' },
  upgraded: { bg: 'bg-blue-100', text: 'text-blue-700' },
  downgraded: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  canceled: { bg: 'bg-red-100', text: 'text-red-700' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-700' },
  renewed: { bg: 'bg-purple-100', text: 'text-purple-700' },
  reactivated: { bg: 'bg-teal-100', text: 'text-teal-700' },
  payment_failed: { bg: 'bg-orange-100', text: 'text-orange-700' },
};

export default function Reports() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<ReportTab>('revenue');
  const [dateRange, setDateRange] = useState(() => ({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date(),
  }));

  const handleExport = async () => {
    try {
      const blob = await api.exportAdminReport({
        report: activeTab,
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with tabs and controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {TAB_CONFIG.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeTab === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            {t('admin.reports.exportCsv', 'Export CSV')}
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'revenue' && <RevenueReport dateRange={dateRange} />}
      {activeTab === 'subscriptions' && <SubscriptionsReport dateRange={dateRange} />}
      {activeTab === 'users' && <UsersReport dateRange={dateRange} />}
      {activeTab === 'ai-costs' && <AICostsReport dateRange={dateRange} />}
    </div>
  );
}

interface ReportProps {
  dateRange: { start: Date; end: Date };
}

function RevenueReport({ dateRange }: ReportProps) {
  const { data: revenue, isLoading } = useQuery({
    queryKey: ['admin-reports-revenue', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: () =>
      api.getAdminRevenueReport({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      }),
  });

  const { data: history } = useQuery({
    queryKey: ['admin-reports-revenue-history', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: () =>
      api.getAdminRevenueHistory({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
        period: 'daily',
      }),
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading revenue data...</div>;
  }

  const tierData = revenue?.byTier
    ? Object.entries(revenue.byTier).map(([tier, data]) => ({
        name: tier === 'pro+' ? 'Pro+' : tier.charAt(0).toUpperCase() + tier.slice(1),
        value: data.mrr,
        count: data.count,
      }))
    : [];

  const platformData = revenue?.byPlatform
    ? Object.entries(revenue.byPlatform).map(([platform, data]) => ({
        name: platform === 'google_play' ? 'Google Play' : platform.charAt(0).toUpperCase() + platform.slice(1),
        value: data.mrr,
        count: data.count,
      }))
    : [];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(revenue?.mrr.current)}
          subtitle={
            revenue?.mrr.changePercent !== undefined
              ? `${revenue.mrr.changePercent >= 0 ? '+' : ''}${revenue.mrr.changePercent}% from last period`
              : undefined
          }
          icon={<DollarSign size={24} />}
          status={revenue?.mrr.change && revenue.mrr.change >= 0 ? 'success' : 'error'}
        />
        <MetricCard
          title="Annual Run Rate"
          value={formatCurrency(revenue?.arr)}
          icon={<TrendingUp size={24} />}
        />
        <MetricCard
          title="Pro Subscribers"
          value={formatNumber(revenue?.byTier?.pro?.count)}
          subtitle={`${formatCurrency(revenue?.byTier?.pro?.mrr)} MRR`}
          icon={<Users size={24} />}
        />
        <MetricCard
          title="Pro+ Subscribers"
          value={formatNumber(revenue?.byTier?.['pro+']?.count)}
          subtitle={`${formatCurrency(revenue?.byTier?.['pro+']?.mrr)} MRR`}
          icon={<Users size={24} />}
          status="success"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">MRR Over Time</h3>
          {history?.history && history.history.length > 0 ? (
            <LineChart
              data={history.history.map((h) => ({
                date: formatDate(h.date),
                mrr: h.cumulativeMrr / 100,
              }))}
              xKey="date"
              yKey="mrr"
              title=""
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No data available for this period
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Tier</h3>
          {tierData.length > 0 ? (
            <PieChart data={tierData} title="" />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No subscription data available
            </div>
          )}
        </div>
      </div>

      {/* Platform Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Platform</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {platformData.map((platform) => (
            <div key={platform.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">{platform.name}</div>
                <div className="text-sm text-gray-500">{platform.count} subscribers</div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900">{formatCurrency(platform.value)}</div>
                <div className="text-sm text-gray-500">MRR</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SubscriptionsReport({ dateRange }: ReportProps) {
  const [page, setPage] = useState(1);
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-reports-subscriptions', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: () =>
      api.getAdminSubscriptionMetrics({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      }),
  });

  const { data: events } = useQuery({
    queryKey: [
      'admin-reports-subscription-events',
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
      page,
      eventTypeFilter,
    ],
    queryFn: () =>
      api.getAdminSubscriptionEvents({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
        page,
        limit: 20,
        event_type: eventTypeFilter || undefined,
      }),
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading subscription data...</div>;
  }

  type SubscriptionEvent = {
    id: string;
    user_id: string;
    user_email: string;
    event_type: string;
    previous_tier: string | null;
    new_tier: string;
    billing_interval: string | null;
    amount_cents: number | null;
    platform: string;
    mrr_change_cents: number | null;
    created_at: string;
  };

  const eventColumns: Array<{
    key: string;
    header: string;
    render?: (item: SubscriptionEvent) => React.ReactNode;
  }> = [
    {
      key: 'created_at',
      header: 'Date',
      render: (item) => formatDateTime(item.created_at),
    },
    {
      key: 'user_email',
      header: 'User',
      render: (item) => (
        <span className="text-sm text-gray-900 truncate max-w-[200px] block">{item.user_email}</span>
      ),
    },
    {
      key: 'event_type',
      header: 'Event',
      render: (item) => {
        const colors = EVENT_TYPE_COLORS[item.event_type] || { bg: 'bg-gray-100', text: 'text-gray-700' };
        return (
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}>
            {item.event_type.replace('_', ' ')}
          </span>
        );
      },
    },
    {
      key: 'previous_tier',
      header: 'From',
      render: (item) => item.previous_tier || '-',
    },
    {
      key: 'new_tier',
      header: 'To',
    },
    {
      key: 'mrr_change_cents',
      header: 'MRR Change',
      render: (item) => {
        if (!item.mrr_change_cents) return '-';
        const isPositive = item.mrr_change_cents > 0;
        return (
          <span className={`flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {formatCurrency(Math.abs(item.mrr_change_cents))}
          </span>
        );
      },
    },
    {
      key: 'platform',
      header: 'Platform',
      render: (item) => (
        <span className="text-xs text-gray-500">
          {item.platform === 'google_play' ? 'Google Play' : item.platform}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Active"
          value={formatNumber(metrics?.active)}
          icon={<Users size={20} />}
        />
        <MetricCard
          title="New"
          value={formatNumber(metrics?.new)}
          subtitle={
            metrics?.newChange !== undefined
              ? `${metrics.newChange >= 0 ? '+' : ''}${metrics.newChange} vs prev`
              : undefined
          }
          icon={<TrendingUp size={20} />}
          status="success"
        />
        <MetricCard
          title="Churned"
          value={formatNumber(metrics?.churned)}
          subtitle={`${metrics?.churnRate || 0}% rate`}
          icon={<TrendingDown size={20} />}
          status="error"
        />
        <MetricCard
          title="Upgrades"
          value={formatNumber(metrics?.upgrades)}
          icon={<ArrowUpRight size={20} />}
        />
        <MetricCard
          title="Downgrades"
          value={formatNumber(metrics?.downgrades)}
          icon={<ArrowDownRight size={20} />}
          status="warning"
        />
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <RefreshCw className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(metrics?.renewals)}</div>
              <div className="text-sm text-gray-500">Renewals</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(metrics?.paymentFailures)}</div>
              <div className="text-sm text-gray-500">Payment Failures</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <RefreshCw className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(metrics?.reactivations)}</div>
              <div className="text-sm text-gray-500">Reactivations</div>
            </div>
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Subscription Events</h3>
          <select
            value={eventTypeFilter}
            onChange={(e) => {
              setEventTypeFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Events</option>
            <option value="created">New</option>
            <option value="upgraded">Upgrades</option>
            <option value="downgraded">Downgrades</option>
            <option value="canceled">Cancellations</option>
            <option value="renewed">Renewals</option>
            <option value="payment_failed">Payment Failures</option>
          </select>
        </div>

        <DataTable
          columns={eventColumns}
          data={events?.events || []}
          loading={!events}
          page={page}
          totalPages={events?.pagination.totalPages || 1}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}

function UsersReport({ dateRange }: ReportProps) {
  const { data: userStats, isLoading } = useQuery({
    queryKey: ['admin-reports-users', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: () =>
      api.getAdminUserReport({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      }),
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading user data...</div>;
  }

  const tierData = userStats?.byTier
    ? Object.entries(userStats.byTier).map(([tier, count]) => ({
        name: tier === 'pro+' ? 'Pro+' : tier.charAt(0).toUpperCase() + tier.slice(1),
        value: count,
      }))
    : [];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={formatNumber(userStats?.total)}
          icon={<Users size={24} />}
        />
        <MetricCard
          title="New Users"
          value={formatNumber(userStats?.new)}
          subtitle={
            userStats?.newChange !== undefined
              ? `${userStats.newChange >= 0 ? '+' : ''}${userStats.newChange} vs prev`
              : undefined
          }
          icon={<TrendingUp size={24} />}
          status="success"
        />
        <MetricCard
          title="Active Users"
          value={formatNumber(userStats?.active)}
          subtitle="Logged in during period"
          icon={<Users size={24} />}
        />
        <MetricCard
          title="Free Users"
          value={formatNumber(userStats?.byTier?.free)}
          icon={<Users size={24} />}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
          {userStats?.growth && userStats.growth.length > 0 ? (
            <LineChart
              data={userStats.growth.map((g) => ({
                date: formatDate(g.date),
                users: g.count,
              }))}
              xKey="date"
              yKey="users"
              title=""
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No growth data available for this period
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by Tier</h3>
          {tierData.length > 0 ? (
            <PieChart data={tierData} title="" />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No tier data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AICostsReport({ dateRange }: ReportProps) {
  const { data: aiCosts, isLoading } = useQuery({
    queryKey: ['admin-reports-ai-costs', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: () =>
      api.getAdminAICostsReport({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      }),
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading AI cost data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Cost"
          value={formatCurrencyDollars(aiCosts?.totals.cost)}
          icon={<DollarSign size={24} />}
        />
        <MetricCard
          title="Total Requests"
          value={formatNumber(aiCosts?.totals.requests)}
          icon={<Bot size={24} />}
        />
        <MetricCard
          title="Total Tokens"
          value={formatNumber(aiCosts?.totals.tokens)}
          icon={<Bot size={24} />}
        />
        <MetricCard
          title="Success Rate"
          value={`${aiCosts?.totals.successRate || 0}%`}
          icon={<TrendingUp size={24} />}
          status={aiCosts?.totals.successRate && aiCosts.totals.successRate >= 95 ? 'success' : 'warning'}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Costs</h3>
          {aiCosts?.daily && aiCosts.daily.length > 0 ? (
            <LineChart
              data={aiCosts.daily.map((d) => ({
                date: formatDate(d.date),
                cost: d.cost,
              }))}
              xKey="date"
              yKey="cost"
              title=""
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No cost data available for this period
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Costs by Use Case</h3>
          {aiCosts?.byUseCase && aiCosts.byUseCase.length > 0 ? (
            <BarChart
              data={aiCosts.byUseCase.map((u) => ({
                name: u.name.replace('_', ' '),
                cost: u.cost,
              }))}
              xKey="name"
              yKey="cost"
              title=""
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No use case data available
            </div>
          )}
        </div>
      </div>

      {/* Provider Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Costs by Provider</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Provider</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Requests</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Tokens</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Cost</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {aiCosts?.byProvider?.map((provider) => (
                <tr key={provider.name} className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">{provider.name}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatNumber(provider.requests)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatNumber(provider.tokens)}</td>
                  <td className="py-3 px-4 text-right text-gray-900 font-medium">
                    {formatCurrencyDollars(provider.cost)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        provider.successRate >= 95
                          ? 'bg-green-100 text-green-700'
                          : provider.successRate >= 80
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {provider.successRate}%
                    </span>
                  </td>
                </tr>
              ))}
              {(!aiCosts?.byProvider || aiCosts.byProvider.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    No provider data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
